import { EWinPresentationStates } from '@Core/Win/States/WinPresentationStates';
import { Game } from '@Game/Game';
import { GameStore } from '@GameStore/GameStore';
import { IAutoPlayState } from '@GFTypes/GameStoreTypes/SpinParameters';
import { EButtonEvents } from '@Managers/Events/ButtonEvents';
import EventsCenter from '@Managers/Events/EventsCenter';
import { EGameEvents } from '@Managers/Events/GameEvents';
import { EGameStates } from '@Managers/StateManager/GameStates';
import {
    autoStartButtonAction,
    autoStopButtonAction,
    betDecreaseButtonAction,
    betIncreaseButtonAction,
    betIndexButtonAction,
    continueButtonAction,
    fullscreenButtonAction,
    gambleButtonAction,
    muteButtonAction,
    skipButtonAction,
    spinButtonAction,
    stopButtonAction,
    takeButtonAction,
    turboButtonAction,
    unmuteButtonAction
} from './ButtonActions';
import {
    onBonusBuyCloseButtonRule,
    onBonusBuyOpenButtonRule,
    onCompleteWinPresentation,
    onGambleButtonRule,
    onGambleEndButtonRule,
    onGambleResultButtonRule,
    onGambleStartButtonRule,
    onIdleButtonRule,
    onMuteButtonRule,
    onPauseButtonRule,
    onPostIntroRule,
    onPostOutroRule,
    onPostRetriggerRule,
    onPreIntroRule,
    onPreOutroRule,
    onPreSpinButtonRule,
    onRefreshBalanceButtonRule,
    onTakeButtonRule,
    onUnmuteButtonRule,
    onUnpauseButtonRule,
    onWildWheelEndButtonRule,
    onWildWheelStartButtonRule,
    onWinEndButtonRule,
    onWinStartButtonRule,
    onWinStateButtonRule
} from './ButtonRules';
import { EBonusBuyEvents } from '@Managers/Events/BonusBuyEvents';

export type TButtonRule = () => void;
export type TButtonAction = (...args: any[]) => void;

export class ButtonManager {
    private readonly game: Game;
    private readonly gameStore: GameStore;
    private readonly rules: Partial<Record<EGameStates | EGameEvents | EBonusBuyEvents | EWinPresentationStates, TButtonRule>> = {
        [EGameStates.IDLE_STATE]: () => onIdleButtonRule(this.game),
        [EGameStates.PRE_SPIN_STATE]: () => onPreSpinButtonRule(this.game),
        [EGameStates.PRE_WIN_STATE]: () => onWinStartButtonRule(this.game),
        [EGameStates.WIN_STATE]: () => onWinStateButtonRule(this.game),
        [EGameStates.POST_WIN_STATE]: () => onWinEndButtonRule(this.game),
        [EWinPresentationStates.POST_WIN_PRESENTATION_STATE]: () => onCompleteWinPresentation(this.game),
        [EGameStates.POST_WIN_FEATURES_STATE]: () => onWinStartButtonRule(this.game),
        [EGameEvents.TAKE]: () => onTakeButtonRule(this.game),
        [EGameStates.PRE_GAMBLE_STATE]: () => onGambleStartButtonRule(this.game),
        [EGameStates.GAMBLE_STATE]: () => onGambleButtonRule(this.game),
        [EGameStates.GAMBLE_RESULT_STATE]: () => onGambleResultButtonRule(this.game),
        [EGameStates.POST_GAMBLE_STATE]: () => onGambleEndButtonRule(this.game),
        [EGameStates.PRE_WILD_WHEEL_STATE]: () => onWildWheelStartButtonRule(this.game),
        [EGameStates.POST_WILD_WHEEL_STATE]: () => onWildWheelEndButtonRule(this.game),
        [EGameEvents.MUTE]: () => onMuteButtonRule(this.game),
        [EGameEvents.UNMUTE]: () => onUnmuteButtonRule(this.game),
        [EGameEvents.REFRESH_BALANCE_PROCESSED]: () => onRefreshBalanceButtonRule(this.game),
        [EGameEvents.PAUSE]: () => onPauseButtonRule(this.game),
        [EGameEvents.UNPAUSE]: () => onUnpauseButtonRule(this.game),
        [EGameStates.PRE_SPECIAL_GAMES_INTRO_STATE]: () => onPreIntroRule(this.game),
        [EGameStates.PRE_SPECIAL_GAMES_OUTRO_STATE]: () => onPreOutroRule(this.game),
        [EGameStates.POST_SPECIAL_GAMES_INTRO_STATE]: () => onPostIntroRule(this.game),
        [EGameStates.POST_SPECIAL_GAMES_OUTRO_STATE]: () => onPostOutroRule(this.game),
        [EGameEvents.SPECIAL_GAMES_RETRIGGER_HIDE]: () => onPostRetriggerRule(this.game),
        [EBonusBuyEvents.BONUS_BUY_DIALOG_CLOSE]: () => onBonusBuyCloseButtonRule(this.game),
        [EBonusBuyEvents.BONUS_BUY_DIALOG_OPEN]: () => onBonusBuyOpenButtonRule(this.game)
    };

    private readonly actions: Record<EButtonEvents, TButtonAction> = {
        [EButtonEvents.AUTO_START_BUTTON_PRESSED]: (settings: IAutoPlayState) => autoStartButtonAction(this.game, settings),
        [EButtonEvents.AUTO_STOP_BUTTON_PRESSED]: () => autoStopButtonAction(this.game),
        [EButtonEvents.BET_INCREASE_BUTTON_PRESSED]: () => betIncreaseButtonAction(this.game),
        [EButtonEvents.BET_DECREASE_BUTTON_PRESSED]: () => betDecreaseButtonAction(this.game),
        [EButtonEvents.BET_INDEX_BUTTON_PRESSED]: ({ betIndex }: { betIndex: number }) => betIndexButtonAction(this.game, betIndex),
        [EButtonEvents.SPIN_BUTTON_PRESSED]: () => spinButtonAction(this.game),
        [EButtonEvents.STOP_BUTTON_PRESSED]: () => stopButtonAction(this.game),
        [EButtonEvents.SKIP_BUTTON_PRESSED]: () => skipButtonAction(this.game),
        [EButtonEvents.TAKE_BUTTON_PRESSED]: () => takeButtonAction(this.game),
        [EButtonEvents.GAMBLE_BUTTON_PRESSED]: () => gambleButtonAction(this.game),
        [EButtonEvents.TURBO_BUTTON_PRESSED]: () => turboButtonAction(this.game),
        [EButtonEvents.AUTO_TURBO_BUTTON_PRESSED]: (options: { level: number }) => turboButtonAction(this.game, true, options),
        [EButtonEvents.MUTE_BUTTON_PRESSED]: () => muteButtonAction(this.game),
        [EButtonEvents.UNMUTE_BUTTON_PRESSED]: () => unmuteButtonAction(this.game),
        [EButtonEvents.FULLSCREEN_BUTTON_PRESSED]: () => fullscreenButtonAction(this.game),
        [EButtonEvents.CONTINUE_BUTTON_PRESSED]: () => continueButtonAction(this.game)
    };

    constructor(game: Game) {
        this.game = game;
        this.gameStore = this.game.gameStore;

        for (const event of Object.keys(this.rules) as EGameEvents[] & EWinPresentationStates[]) {
            const rule = this.rules[event];
            if (rule) EventsCenter.on(event, rule, this.game);
        }

        for (const event of Object.keys(this.actions) as EButtonEvents[]) {
            const action = this.actions[event];
            if (action) EventsCenter.on(event, action, this.game);
        }
    }
}
